import { defineStore } from 'pinia';
import moment from 'moment';
import { ownPoint, getMySubscriptions } from '@/api/PaymentApi';
import { nextTick, onMounted } from 'vue';
import { getTicketCountAPI, getAnalysisTicketAPI, getAnalysisContentAPI } from '@/api/AnalysisApi';
import { DEV_MODE } from '@/config/dev-mode';

export const usePaymentsStore = defineStore('payments', () => {
  /**
   * 활성 구독 목록 (정식 GET /subscriptions, status=ACTIVE)
   * - 진입점(복지찬스 구독 버튼)의 구독중/구독하기 분기에 사용
   * - 관리자 부여로도 생길 수 있어 라우트 이동마다 재검증하되, TTL 로 호출 폭주 방지
   */
  const SUBSCRIPTION_TTL_MS = 30_000;
  const activeSubscriptions = ref([]);
  const activeSubscriptionFetchedAt = ref(0);
  let activeSubscriptionInflight = null;
  /** 활성 구독 보유 여부 */
  const isSubscribed = computed(() => activeSubscriptions.value.length > 0);
  /** 대표 활성 구독 (목록 첫 항목) */
  const activeSubscription = computed(() => activeSubscriptions.value[0] ?? null);
  /**
   * 해지 신청됨(ACTIVE 유지·기간 만료 전) 여부 → "해지 예정" 표시용
   * - eventLogs 의 해지 신청(CANCEL_REQUESTED)/철회(CANCEL_RESUMED) 중 가장 최근 이벤트로 판별
   *   (신청→철회 시 cancelRequestedAt 이 null 로 비워질 수 있어 이력 기준이 더 정확)
   * - eventLogs 가 없으면 cancelRequestedAt 로 폴백
   */
  const isSubscriptionCancelRequested = computed(() => {
    const sub = activeSubscription.value;
    if (!sub || sub.endedAt) return false;
    const logs = Array.isArray(sub.eventLogs) ? sub.eventLogs : [];
    const lifecycle = logs
      .filter((l) => l.eventType === 'CANCEL_REQUESTED' || l.eventType === 'CANCEL_RESUMED')
      .sort((a, b) => String(a.occurredAt).localeCompare(String(b.occurredAt)));
    if (lifecycle.length) {
      return lifecycle[lifecycle.length - 1].eventType === 'CANCEL_REQUESTED';
    }
    return !!sub.cancelRequestedAt;
  });
  /** 월 전체 일진 구매 여부 */
  const isMonthlyFortunePaid = ref(false);

  /** 택일 라이트 분석권 개수 */
  const lightTicketCount = ref(0);
  /** 택일 집중 분석권 개수 */
  const concentratedTicketCount = ref(0);

  /** 택일 분석권 상세 (기간별: 7일권, 30일권) - count와 ticketId 포함 */
  const choiceTicketDetails = ref({
    light: {
      day7: { count: 0, ticketId: null },
      day30: { count: 0, ticketId: null },
    },
    concentrated: {
      day7: { count: 0, ticketId: null },
      day30: { count: 0, ticketId: null },
    },
  });

  /** 낭만일진 미니팩 분석권 개수 (analysisTypeId: 36) */
  const dailyMiniPackTicketCount = ref(0);
  /** 낭만일진 풀팩 분석권 개수 (analysisTypeId: 35) */
  const dailyFullPackTicketCount = ref(0);

  /** 낭만일진 분석권 상세 데이터 */
  const dailyFortuneTicketDetails = ref({
    miniPack: null, // { id, totalCount, ... }
    fullPack: null,
  });

  /** 낭만궁합 분석권 상세 (인원수별: 2인/3인/4인) */
  const harmonyTicketDetails = ref({
    members2: { count: 0, ticketId: null },
    members3: { count: 0, ticketId: null },
    members4: { count: 0, ticketId: null },
  });

  /** 낭만작명 분석권 (analysisTypeId: 47) — 총합 + 옵션별 보유수 */
  const namingTicketCount = ref(0);
  const namingTicketId = ref(null);
  /** 옵션별 보유수 맵 { 'opt:62': 2, 'mode:recommend-person-kor': 2, ... }. 옵션 분리 없으면 null */
  const namingTicketDetails = ref(null);

  /** 티켓 조회 중복 요청 방지 */
  const isTicketFetching = ref(false);
  let ticketFetchPromise = null;

  /** 포인트 조회 중복 요청 방지 */
  const isPointsFetching = ref(false);

  /** 보유 포인트 */
  const points = ref(null);

  /** 포인트 포맷팅 */
  const formattedPoints = computed(() => {
    if (points.value === null) return 0;
    else if (points.value < 1000) return points.value;
    else return points.value.toLocaleString('en-US');
  });

  /**
   * 활성 구독 조회 (GET /subscriptions?status=ACTIVE)
   * - 관리자 부여로도 구독이 생길 수 있어 라우트 이동마다 호출하되, TTL(30s) 내면 캐시 반환.
   * - force=true 면 TTL 무시 재조회 (구독/해지 직후 즉시 갱신용).
   * - 동시 호출은 in-flight 가드로 1회로 합침.
   */
  const fetchActiveSubscriptions = async (force = false) => {
    const now = Date.now();
    if (
      !force &&
      activeSubscriptionFetchedAt.value &&
      now - activeSubscriptionFetchedAt.value < SUBSCRIPTION_TTL_MS
    ) {
      return activeSubscriptions.value;
    }
    if (activeSubscriptionInflight) return activeSubscriptionInflight;
    activeSubscriptionInflight = (async () => {
      try {
        const result = await getMySubscriptions({ status: 'ACTIVE' });
        activeSubscriptions.value = Array.isArray(result) ? result : [];
        activeSubscriptionFetchedAt.value = Date.now();
        DEV_MODE &&
          console.log(
            `[구독 체크] active=${activeSubscriptions.value.length}`,
            JSON.stringify(activeSubscriptions.value),
          );
      } catch (e) {
        activeSubscriptions.value = [];
        DEV_MODE && console.log('[구독 체크] 실패', e?.response?.status);
      } finally {
        activeSubscriptionInflight = null;
      }
      return activeSubscriptions.value;
    })();
    return activeSubscriptionInflight;
  };

  /** 보유 포인트 조회 */
  const getOwnPoint = async () => {
    // 이미 요청 진행 중이면 스킵
    if (isPointsFetching.value) return points.value;

    isPointsFetching.value = true;
    try {
      const result = await ownPoint({});
      points.value = result;
      return result;
    } catch (e) {
      points.value = null;
      return null;
    } finally {
      isPointsFetching.value = false;
    }
  };

  /** 구독/포인트/분석권 전체 초기화 (로그아웃 시 호출) */
  const clearPaymentsInfo = () => {
    // 구독 정보
    activeSubscriptions.value = [];
    activeSubscriptionFetchedAt.value = 0;

    // 포인트
    points.value = null;

    // 분석권
    analysisTicketCount.value = null;
    analysisTicketsData.value = null;

    // 택일 분석권 상세
    lightTicketCount.value = 0;
    concentratedTicketCount.value = 0;
    choiceTicketDetails.value = {
      light: {
        day7: { count: 0, ticketId: null },
        day30: { count: 0, ticketId: null },
      },
      concentrated: {
        day7: { count: 0, ticketId: null },
        day30: { count: 0, ticketId: null },
      },
    };

    // 낭만일진 분석권
    dailyMiniPackTicketCount.value = 0;
    dailyFullPackTicketCount.value = 0;
    dailyFortuneTicketDetails.value = {
      miniPack: null,
      fullPack: null,
    };

    // 낭만궁합 분석권
    harmonyTicketDetails.value = {
      members2: { count: 0, ticketId: null },
      members3: { count: 0, ticketId: null },
      members4: { count: 0, ticketId: null },
    };

    // 낭만작명 분석권
    namingTicketCount.value = 0;
    namingTicketId.value = null;
    namingTicketDetails.value = null;

    // 월 전체 일진 구매 정보
    isMonthlyFortunePaid.value = false;
    tempPurchaseList.value = [];
    sessionStorage.removeItem('purchaseList');
  };

  // '월 전체 일진 구매' 임시 로직
  const tempPurchaseList = ref([]);
  const tempAddPurchaseList = (viewTitle) => {
    tempPurchaseList.value.push(viewTitle);
    tempUpdateIsMonthlyFortunePaid(viewTitle);

    nextTick(() => {
      sessionStorage.setItem('purchaseList', JSON.stringify(tempPurchaseList.value));
    });
  };
  const tempUpdateIsMonthlyFortunePaid = (viewTitle) => {
    const matchData = tempPurchaseList.value.find((data) => data === viewTitle);
    if (matchData) {
      isMonthlyFortunePaid.value = true;
    } else {
      isMonthlyFortunePaid.value = false;
    }
  };

  /** 보유 분석권 개수 */
  const analysisTicketCount = ref(null);

  /** 보유 분석권 상세  */
  const analysisTicketsData = ref(null);

  /** 보유 분석권 조회 (CreditBar 표시용) */
  const fetchTicketCount = async () => {
    // 이미 요청 진행 중이면 동일 Promise를 공유해서 대기
    if (isTicketFetching.value && ticketFetchPromise) return ticketFetchPromise;

    isTicketFetching.value = true;
    ticketFetchPromise = (async () => {
    try {
      const result = await getTicketCountAPI();
      analysisTicketCount.value = result.count;
      analysisTicketsData.value = result.tickets;

      // 낭만택일 (집중/라이트) 파싱 로직 - count와 ticketId 포함
      const details = {
        light: {
          day7: { count: 0, ticketId: null },
          day30: { count: 0, ticketId: null },
        },
        concentrated: {
          day7: { count: 0, ticketId: null },
          day30: { count: 0, ticketId: null },
        },
      };

      let lightTotal = 0;
      let concentratedTotal = 0;

      if (result.tickets) {
        for (const ticket of result.tickets) {
          if (ticket.typeId === 13 || ticket.typeId === 14) {
            if (ticket.options) {
              for (const opt of ticket.options) {
                const isLight = opt.typeId === 14 || opt.name?.includes('라이트');
                const typeKey = isLight ? 'light' : 'concentrated';

                if (isLight) {
                  lightTotal += opt.count || 0;
                } else {
                  concentratedTotal += opt.count || 0;
                }

                if (opt.name?.includes('7일')) {
                  details[typeKey].day7.count = opt.count;
                } else if (opt.name?.includes('30일')) {
                  details[typeKey].day30.count = opt.count;
                }
              }
            }
          }
        }
      }

      lightTicketCount.value = lightTotal;
      concentratedTicketCount.value = concentratedTotal;
      choiceTicketDetails.value = details;

      // 낭만일진 분석권 파싱 (typeId: 35=풀팩, 36=미니팩)
      let miniPackTotal = 0;
      let fullPackTotal = 0;
      let miniPackData = null;
      let fullPackData = null;

      if (result.tickets) {
        for (const ticket of result.tickets) {
          // typeId: 35 = 풀팩
          if (ticket.typeId === 35) {
            fullPackTotal = ticket.totalCount || 0;
            fullPackData = { ...ticket };
          }
          // typeId: 36 = 미니팩
          else if (ticket.typeId === 36) {
            miniPackTotal = ticket.totalCount || 0;
            miniPackData = { ...ticket };
          }
        }
      }

      dailyMiniPackTicketCount.value = miniPackTotal;
      dailyFullPackTicketCount.value = fullPackTotal;
      // 기존 analysisTicketId 유지하면서 count 데이터만 업데이트
      dailyFortuneTicketDetails.value = {
        miniPack: miniPackData
          ? {
              ...miniPackData,
              analysisTicketId: dailyFortuneTicketDetails.value.miniPack?.analysisTicketId,
              ticketData: dailyFortuneTicketDetails.value.miniPack?.ticketData,
            }
          : null,
        fullPack: fullPackData
          ? {
              ...fullPackData,
              analysisTicketId: dailyFortuneTicketDetails.value.fullPack?.analysisTicketId,
              ticketData: dailyFortuneTicketDetails.value.fullPack?.ticketData,
            }
          : null,
      };

      // 낭만궁합 분석권 파싱 (typeId: 38)
      parseHarmonyTicketDetails();

      // 낭만작명 분석권 파싱 (typeId: 47) — 총합 + 옵션별
      parseNamingTicketDetails();
    } catch (e) {
      analysisTicketCount.value = null;
    } finally {
      isTicketFetching.value = false;
    }
    })();
    try {
      return await ticketFetchPromise;
    } finally {
      ticketFetchPromise = null;
    }
  };

  /** 택일 분석권 상세 조회 (결제용 analysisTicketId 포함) */
  const fetchChoiceTicketDetails = async () => {
    try {
      const userId = sessionStorage.getItem('authUserId');
      if (!userId) throw new Error('User not logged in');

      const ticketListResult = await getAnalysisTicketAPI({
        analysisTypeId: 13,
        includeChildTypes: true,
        isAvailable: true,
        userId,
      });

      if (ticketListResult?.content) {
        const details = { ...choiceTicketDetails.value };
        for (const ticket of ticketListResult.content) {
          // analysisTypeId: 13=집중, 14=라이트
          const typeKey = ticket.analysisTypeId === 14 ? 'light' : 'concentrated';

          if (ticket.monthRange === 1) {
            // 30일권
            details[typeKey].day30.ticketId = ticket.analysisTicketId;
          } else if (ticket.dayRange === 7) {
            // 7일권
            details[typeKey].day7.ticketId = ticket.analysisTicketId;
          }
        }
        choiceTicketDetails.value = details;
      }
    } catch (e) {
      console.error('Failed to fetch choice ticket details:', e);
    }
  };

  /** 낭만일진 분석권 상세 조회 (결제용 analysisTicketId 포함) */
  const fetchDailyFortuneTicketDetails = async () => {
    try {
      const userId = sessionStorage.getItem('authUserId');
      if (!userId) throw new Error('User not logged in');

      // 부모 타입 35로 조회하면 35(풀팩)와 36(미니팩) 모두 조회됨
      const result = await getAnalysisTicketAPI({
        analysisTypeId: 35,
        includeChildTypes: true,
        isAvailable: true,
        userId,
      });

      if (result?.content) {
        // 택일처럼 객체 복사 후 마지막에 한번에 할당
        const details = {
          miniPack: dailyFortuneTicketDetails.value.miniPack
            ? { ...dailyFortuneTicketDetails.value.miniPack }
            : null,
          fullPack: dailyFortuneTicketDetails.value.fullPack
            ? { ...dailyFortuneTicketDetails.value.fullPack }
            : null,
        };

        // 남은 티켓 수량 계산 (count - countUsed)
        let miniPackTotal = 0;
        let fullPackTotal = 0;

        for (const ticket of result.content) {
          // 남은 수량 = count - countUsed
          const remainingCount = (ticket.count || 0) - (ticket.countUsed || 0);

          // analysisTypeId: 35=풀팩, 36=미니팩
          if (ticket.analysisTypeId === 35) {
            fullPackTotal += remainingCount;
            details.fullPack = {
              ...(details.fullPack || {}),
              analysisTicketId: ticket.analysisTicketId,
              ticketData: ticket,
            };
          } else if (ticket.analysisTypeId === 36) {
            miniPackTotal += remainingCount;
            details.miniPack = {
              ...(details.miniPack || {}),
              analysisTicketId: ticket.analysisTicketId,
              ticketData: ticket,
            };
          }
        }

        // 티켓 수량 업데이트
        dailyMiniPackTicketCount.value = miniPackTotal;
        dailyFullPackTicketCount.value = fullPackTotal;
        dailyFortuneTicketDetails.value = details;
      }
    } catch (e) {
      console.error('Failed to fetch daily fortune ticket details:', e);
    }
  };

  /** 낭만궁합 분석권 상세 파싱 (fetchTicketCount의 analysisTicketsData에서 추출) */
  const parseHarmonyTicketDetails = () => {
    const details = {
      members2: { count: 0, ticketId: null },
      members3: { count: 0, ticketId: null },
      members4: { count: 0, ticketId: null },
    };

    const tickets = analysisTicketsData.value;
    const harmonyTicket = tickets?.find((t) => t.typeId === 38);

    if (!harmonyTicket) {
      // typeId 38이 응답에서 빠지면 잔여 분석권 없음 → 0으로 초기화
      harmonyTicketDetails.value = details;
      return;
    }

    if (harmonyTicket.options) {
      for (const opt of harmonyTicket.options) {
        // name에서 인원수 추출 (예: "3명" → 3)
        const match = opt.name?.match(/(\d+)/);
        const mc = match ? Number(match[1]) : null;
        if (mc >= 2 && mc <= 4) {
          const key = `members${mc}`;
          details[key].count = opt.count || 0;
        }
      }
    } else {
      details.members2.count = harmonyTicket.totalCount || 0;
    }

    harmonyTicketDetails.value = details;
  };

  /** 낭만궁합 분석권 낙관적 차감 (결제 성공 직후 호출 — 서버 반영 지연 대비) */
  const decrementHarmonyTicket = (memberCount) => {
    const key = `members${memberCount}`;
    const current = harmonyTicketDetails.value[key];
    if (!current) return;
    const nextCount = Math.max(0, (current.count || 0) - 1);
    harmonyTicketDetails.value = {
      ...harmonyTicketDetails.value,
      [key]: { ...current, count: nextCount },
    };
  };

  /** 옵션/티켓옵션 객체에서 식별 키 후보 배열 (optionId > modeKey > name) */
  const namingTicketOptionKeys = (o) =>
    [
      o?.optionId != null ? `opt:${o.optionId}` : null,
      o?.modeKey ? `mode:${o.modeKey}` : null,
      o?.name ? `name:${o.name}` : null,
    ].filter(Boolean);

  /**
   * 낭만작명 분석권 옵션별 파싱 (fetchTicketCount의 analysisTicketsData에서 추출).
   * 응답이 옵션 분리(ticket.options[])면 옵션별 맵을, 아니면 단일 총합만 반영.
   * 옵션 식별 형태(optionId/modeKey/name)를 모르므로 가능한 키를 모두 저장해 양방향 매칭한다.
   */
  const parseNamingTicketDetails = () => {
    const tickets = analysisTicketsData.value;
    const namingTicket = tickets?.find((t) => t.typeId === 47);
    if (!namingTicket) {
      namingTicketCount.value = 0;
      namingTicketDetails.value = null;
      return;
    }
    namingTicketCount.value = namingTicket.totalCount || 0;

    if (namingTicket.options?.length) {
      const map = {};
      for (const opt of namingTicket.options) {
        const cnt = opt.count || 0;
        for (const key of namingTicketOptionKeys(opt)) {
          map[key] = (map[key] || 0) + cnt;
        }
      }
      namingTicketDetails.value = map;
    } else {
      namingTicketDetails.value = null;
    }
  };

  /** 특정 옵션의 보유 분석권 수 (옵션 분리 없으면 단일 총합 반환) */
  const getNamingTicketCount = (option) => {
    const details = namingTicketDetails.value;
    if (!details) return namingTicketCount.value;
    for (const key of namingTicketOptionKeys(option)) {
      if (details[key] != null) return details[key];
    }
    return 0;
  };

  /** 낭만작명 분석권 낙관적 차감 (결제 성공 직후 호출, 옵션별) */
  const decrementNamingTicket = (option) => {
    namingTicketCount.value = Math.max(0, (namingTicketCount.value || 0) - 1);
    const details = namingTicketDetails.value;
    if (!details || !option) return;
    for (const key of namingTicketOptionKeys(option)) {
      if (details[key] != null) {
        namingTicketDetails.value = { ...details, [key]: Math.max(0, details[key] - 1) };
        return;
      }
    }
  };

  /** 낭만작명 결제용 analysisTicketId 조회 (결제 직전 호출) */
  const fetchNamingTicketId = async () => {
    try {
      const result = await getAnalysisTicketAPI({
        analysisTypeId: 47,
        includeChildTypes: true,
        isAvailable: true,
      });
      const ticket = result?.content?.[0];
      if (ticket?.analysisTicketId) {
        namingTicketId.value = ticket.analysisTicketId;
        return ticket.analysisTicketId;
      }
      return null;
    } catch (e) {
      console.error('Failed to fetch naming ticket id:', e);
      return null;
    }
  };

  /** 낭만궁합 결제용 analysisTicketId 조회 (결제 직전 호출) */
  const fetchHarmonyTicketId = async (memberCount) => {
    try {
      const result = await getAnalysisTicketAPI({
        analysisTypeId: 38,
        includeChildTypes: true,
        isAvailable: true,
      });

      if (result?.content) {
        const ticket = result.content.find((t) => t.memberCount === memberCount);
        if (ticket) {
          const key = `members${memberCount}`;
          harmonyTicketDetails.value = {
            ...harmonyTicketDetails.value,
            [key]: {
              ...harmonyTicketDetails.value[key],
              ticketId: ticket.analysisTicketId,
            },
          };
          return ticket.analysisTicketId;
        }
      }
      return null;
    } catch (e) {
      console.error('Failed to fetch harmony ticket id:', e);
      return null;
    }
  };

  /** 분석 옵션(가격 정보) 조회 */
  const fetchAnalysisOptions = async () => {
    try {
      const results = await Promise.allSettled([
        getAnalysisContentAPI({ analysisTypeId: 13 }), // Concentrated
        getAnalysisContentAPI({ analysisTypeId: 14 }), // Light
      ]);

      const options = {
        concentrated: [],
        light: [],
      };

      if (results[0].status === 'fulfilled' && results[0].value?.options) {
        options.concentrated = results[0].value.options;
      }

      if (results[1].status === 'fulfilled' && results[1].value?.options) {
        options.light = results[1].value.options;
      }

      return options;
    } catch (e) {
      console.error('Failed to fetch analysis options:', e);
      return { concentrated: [], light: [] };
    }
  };

  onMounted(() => {
    const stored = sessionStorage.getItem('purchaseList');
    if (stored) {
      tempPurchaseList.value = JSON.parse(stored);
      tempUpdateIsMonthlyFortunePaid(moment().format('YYYY년 M월'));
    }
  });

  // ===== 모바일 이용중인 서비스 상태 관리 =====
  const pointHistoryCache = ref([]);
  const pointHistoryPage = ref(1);
  const pointHistoryTotalPages = ref(0);
  const pointHistoryScrollY = ref(0);
  const insetBottom = ref(0);

  const setPointHistory = ({ list, page, totalPages }) => {
    pointHistoryCache.value = list;
    pointHistoryPage.value = page;
    pointHistoryTotalPages.value = totalPages;
  };

  const setPointHistoryScroll = (y) => {
    pointHistoryScrollY.value = y;
  };

  const clearPointHistory = () => {
    pointHistoryCache.value = [];
    pointHistoryPage.value = 1;
    pointHistoryTotalPages.value = 0;
    pointHistoryScrollY.value = 0;
  };

  const setInsetBottom = (inset) => {
    insetBottom.value = inset;
  };

  /** 포인트 + 분석권 일괄 조회 */
  const fetchPointsAndTickets = async () => {
    await Promise.all([getOwnPoint(), fetchTicketCount()]);
  };

  return {
    activeSubscriptions,
    activeSubscription,
    isSubscribed,
    isSubscriptionCancelRequested,
    fetchActiveSubscriptions,
    isMonthlyFortunePaid,
    formattedPoints,
    points,
    getOwnPoint,
    fetchPointsAndTickets,
    clearPaymentsInfo,
    tempAddPurchaseList,
    tempUpdateIsMonthlyFortunePaid,
    fetchTicketCount,
    analysisTicketCount,
    analysisTicketsData,

    // 택일 분석권 (라이트/집중)
    lightTicketCount,
    concentratedTicketCount,
    choiceTicketDetails,
    fetchChoiceTicketDetails,

    // 낭만일진 분석권 (미니팩/풀팩)
    dailyMiniPackTicketCount,
    dailyFullPackTicketCount,
    dailyFortuneTicketDetails,
    fetchDailyFortuneTicketDetails,

    // 낭만궁합 분석권 (인원수별)
    harmonyTicketDetails,
    parseHarmonyTicketDetails,
    fetchHarmonyTicketId,
    decrementHarmonyTicket,

    // 낭만작명 분석권 (옵션별)
    namingTicketCount,
    namingTicketId,
    namingTicketDetails,
    fetchNamingTicketId,
    getNamingTicketCount,
    decrementNamingTicket,

    // 분석 옵션 (가격 정보)
    fetchAnalysisOptions,

    //  모바일 이용중인 서비스 상태 관리
    pointHistoryCache,
    pointHistoryPage,
    pointHistoryTotalPages,
    pointHistoryScrollY,
    insetBottom,
    setPointHistory,
    setPointHistoryScroll,
    clearPointHistory,
    setInsetBottom,
  };
});
